import type { CaseStudy } from '../types';

export const standardOil: CaseStudy = {
  id: 'standard-oil',
  order: 9,
  title: '标准石油',
  english: 'Standard Oil',
  category: 'power',
  era: '1870 — 1911',
  region: '美国',
  accent: '#7a5b76',
  hook: '它用规模、垂直整合和铁路运价优势，把炼油变成少数人控制的系统。效率与排挤竞争对手，常常发生在同一套动作里。',
  question: '一个能把油价降下来的巨头，为什么最终被最高法院拆分？',
  summary:
    '标准石油的崛起同时包含真实的规模效率、对物流与渠道的控制，以及压制竞争的行为。1911 年的拆分不是“效率有罪”，而是法律对垄断权力与竞争秩序的一次划界。',
  thesis: '“价格更低”不能自动证明市场健康；还要看低价来自效率，还是来自让对手无法生存的控制力。',
  quote: '效率让企业变大，权力让企业难以被替代；监管要分辨的是这两者，而不是简单地赞美或厌恶规模。',
  entryNodeIds: ['standard-scale', 'standard-rail', 'standard-trust'],
  focusNodeId: 'standard-rail',
  lensIds: ['scale', 'scarcity', 'incentives', 'regulation', 'asymmetry', 'collective-action'],
  opportunityIds: ['opp-regulatory-wedge'],
  sourceIds: ['standard-oil-1911'],
  facts: [
    {
      text: '标准石油 1870 年在俄亥俄州注册成立。到 1880 年代，它已控制美国炼油业的绝大部分产能，历史叙述常引用约 90% 的份额。',
      sourceIds: ['standard-oil-1911'],
    },
    {
      text: '1882 年，标准石油托拉斯成立，把多家州公司置于统一管理之下；这种结构本身是应对各州公司法限制的法律创新。',
      sourceIds: ['standard-oil-1911'],
    },
    {
      text: '1911 年，美国最高法院依据《谢尔曼反托拉斯法》裁定标准石油非法垄断，并要求将其拆分为 34 家公司。',
      sourceIds: ['standard-oil-1911'],
    },
  ],
  nodes: [
    {
      id: 'standardoil',
      caseId: 'standard-oil',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '标准石油',
      summary: '从炼油厂到托拉斯，再到被最高法院拆分。',
      detail:
        '标准石油起于炼油，逐步整合管道、运输、仓储与销售。它通过规模降低单位成本，也通过铁路运价安排、收购与排他性手段压缩竞争空间。理解它，不能只选“效率英雄”或“垄断恶棍”一个版本。',
      boundary: '19 世纪美国工业与法律环境与今天差异很大；不能把当时的做法直接类比为现代企业的合法策略。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-scale',
      caseId: 'standard-oil',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '规模与垂直整合',
      summary: '炼油、管道、仓储和销售被纳入同一体系，单位成本与协调成本同时下降。',
      detail:
        '炼油是资本密集型业务，产能利用率越高，单位成本越低。标准石油整合上下游，可以减少中间商加价、稳定供应，并把副产品变成收入。规模效率是真实的，也是它能长期压低价格的原因之一。',
      boundary: '规模效率不能证明所有排他行为都合理；效率收益也可能被用来补贴掠夺性定价。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-rail',
      caseId: 'standard-oil',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '铁路运价：控制物流节点',
      summary: '通过运价折扣、回扣与货运安排，标准石油获得对手没有的成本优势。',
      detail:
        '在铁路是主要长途运输方式的时代，运价决定了炼油厂的竞争边界。标准石油凭借大体量谈判运价，甚至获得对竞争对手货运的“回扣”安排。这既是规模议价，也把物流节点变成了排他工具。',
      boundary: '并非所有运价优惠都违法；关键在于是否通过排他安排把成本优势转化为对手无法竞争的结构。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-trust',
      caseId: 'standard-oil',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '托拉斯：用法律结构统一控制',
      summary: '把分散的州公司股权集中到受托人手里，绕过各州对跨州持股的限制。',
      detail:
        '19 世纪美国公司法以州为单位，企业跨州整合面临限制。托拉斯把多家公司的股份交给少数受托人，实现统一经营。它是法律工程：当市场整合遇到制度摩擦，企业会发明新的组织形态。',
      boundary: '托拉斯本身是组织工具，不必然违法；违法性取决于是否用于限制竞争。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-antitrust',
      caseId: 'standard-oil',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '反垄断：用法律重新划界',
      summary: '1890 年《谢尔曼法》把“限制贸易的契约、合并与共谋”纳入联邦监管。',
      detail:
        '当企业规模越过一定边界，竞争秩序与政治权力成为公共问题。反垄断法不是反对“大”本身，而是反对用排他行为维持或获取垄断力量。1911 年的判决既承认规模，也划出了行为边界。',
      boundary: '反垄断执法受经济学范式与政治周期影响，宽严标准会变化。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-condition',
      caseId: 'standard-oil',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：铁路时代与煤油需求',
      summary: '铁路是稀缺的长途物流网络，照明与工业需求让炼油规模迅速扩大。',
      detail:
        '标准石油的权力建立在铁路与煤油两个基础之上：铁路决定运输成本，煤油是当时的主要照明燃料。没有铁路网络与集中需求，炼油厂很难形成全国性控制。',
      boundary: '电力普及、汽车与石油化工兴起后，行业结构再次改变。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-outcome',
      caseId: 'standard-oil',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：拆分与遗产',
      summary: '标准石油被拆成 34 家公司，部分后代成为今日能源巨头。',
      detail:
        '拆分并没有让石油行业回到小作坊竞争，而是形成了新的寡头结构。标准石油的许多做法——规模整合、管道控制、品牌与分销——被继承下来；被改变的是法律允许的竞争边界。',
      boundary: '拆分的长期经济效果存在争论；股东、消费者与竞争者的得失并不一致。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-risk',
      caseId: 'standard-oil',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：效率叙事掩盖排他',
      summary: '“我们让油价更便宜”可以同时是真的，也可以同时是压制竞争的理由。',
      detail:
        '规模效率、低价与消费者福利是真实存在的；但如果低价靠的是让对手无法获得铁路运力、融资或渠道，那么短期低价可能换来长期更高的价格与更少的创新。判断时要区分“做得更好”和“让别人做不下去”。',
      boundary: '不是所有低价竞争都是掠夺性定价；需要看成本、回收期、排他行为与市场进入条件。',
      evidence: 'opinion',
      confidence: 'high',
      sourceIds: ['standard-oil-1911'],
    },
    {
      id: 'standard-opp',
      caseId: 'standard-oil',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：监管变化会重排价值链',
      summary: '每一次反垄断、环保或能源政策变化，都会改变谁可以控制哪个节点。',
      detail:
        '对创业者和投资者，监管不是只带来成本，也会创造新的接口与合规服务：拆分后的资产重组、管道与储能、合规审计，以及替代能源的“卖铲子”生意。关键不是预测法规细节，而是理解哪一类权力会被限制、哪一类稀缺会重新出现。',
      boundary: '监管驱动的机会高度依赖政治与执法节奏，容易过早下注；合规能力与资本门槛也不低。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['standard-oil-1911'],
    },
  ],
  edges: [
    {
      id: 'standard-e1',
      caseId: 'standard-oil',
      from: 'standardoil',
      to: 'standard-scale',
      label: '整合产能',
      explanation: '通过并购与建设，把炼油、管道、仓储和销售纳入统一体系。',
      tone: 'cause',
    },
    {
      id: 'standard-e2',
      caseId: 'standard-oil',
      from: 'standardoil',
      to: 'standard-rail',
      label: '绑定物流',
      explanation: '巨大的运量让公司获得铁路议价权，也把运输节点变成竞争工具。',
      tone: 'cause',
    },
    {
      id: 'standard-e3',
      caseId: 'standard-oil',
      from: 'standard-scale',
      to: 'standard-trust',
      label: '整合需要组织',
      explanation: '跨州整合遇到公司法限制，托拉斯成为统一控制的法律结构。',
      tone: 'cause',
    },
    {
      id: 'standard-e4',
      caseId: 'standard-oil',
      from: 'standard-rail',
      to: 'standard-outcome',
      label: '成本优势',
      explanation: '运价折扣与回扣让标准石油的到岸成本低于竞争对手。',
      tone: 'cause',
    },
    {
      id: 'standard-e5',
      caseId: 'standard-oil',
      from: 'standard-scale',
      to: 'standard-outcome',
      label: '效率与低价',
      explanation: '规模与垂直整合降低了单位成本，使更低价格成为可能。',
      tone: 'cause',
    },
    {
      id: 'standard-e6',
      caseId: 'standard-oil',
      from: 'standard-trust',
      to: 'standard-outcome',
      label: '统一控制',
      explanation: '托拉斯让多家公司按同一策略行动，减少内部竞争。',
      tone: 'cause',
    },
    {
      id: 'standard-e7',
      caseId: 'standard-oil',
      from: 'standard-antitrust',
      to: 'standard-outcome',
      label: '法律反制',
      explanation: '《谢尔曼法》与最高法院判决改变了垄断结构的合法性边界。',
      tone: 'risk',
    },
    {
      id: 'standard-e8',
      caseId: 'standard-oil',
      from: 'standard-condition',
      to: 'standard-outcome',
      label: '时代前提',
      explanation: '铁路网络与煤油需求，让全国性炼油整合既有必要也可行。',
      tone: 'enable',
    },
    {
      id: 'standard-e9',
      caseId: 'standard-oil',
      from: 'standard-outcome',
      to: 'standard-risk',
      label: '权力留下问题',
      explanation: '拆分改变了结构，但规模、整合与市场权力的问题并未消失。',
      tone: 'risk',
    },
    {
      id: 'standard-e10',
      caseId: 'standard-oil',
      from: 'standard-risk',
      to: 'standard-opp',
      label: '规则变化创造空间',
      explanation: '当法律重新划定竞争边界，新的资产、合规与服务需求会出现。',
      tone: 'enable',
    },
  ],
};

